import { RESERVATION_DEFAULT_SLOT_INTERVAL_MIN } from '@/interfaces/IReservationSettings';
import type {
  IReservationSettings,
  IReservationSettingsConfiguration,
  TReservationSlotInterval,
} from '@/interfaces/IReservationSettings';
import { shortWeekdaysMonFirst } from '@/utils/intl-weekdays.utils';
import {
  isBookableReservationTable,
  isOnlineReservationTable,
} from './reservation-tables';
import { MAX_CREATED_TABLES } from './reservation-tables-draft';

export { usesMenuQrVoseo as usesReservationVoseo } from '@/domain/menu-qr/menu-qr-copy';

export const RESERVATION_GUIDE_STEPS = [
  'hours',
  'tables',
  'groups',
  'alerts',
] as const;
export type TReservationGuideStep = typeof RESERVATION_GUIDE_STEPS[number];
export type TReservationGuideScreen = TReservationGuideStep | 'done';
export type TReservationGuideFinishState = 'ready' | 'live' | 'phoneOnly';
export type TGuestPreviewFocus = 'time' | 'party' | 'contact' | 'off' | 'live';
export type TActivationBlocker = 'shifts' | 'tables' | null;

const SCREENS: readonly TReservationGuideScreen[] = [
  ...RESERVATION_GUIDE_STEPS,
  'done',
];

/**
 * D16: only being enabled or having walked past Avisos (the staff email is
 * the one field that step alone writes) is evidence of progress; seats may
 * pre-date the guide. An enabled branch that still cannot take reservations
 * (no shift, or no table to seat) resumes at the step that fixes it.
 */
export function resumeReservationGuide(
  configuration: IReservationSettingsConfiguration
): TReservationGuideScreen {
  const { enabled, onlineEnabled, staffEmail } = configuration.settings;
  if (enabled) {
    const blocker = activationBlocker(configuration, onlineEnabled);
    if (blocker === 'shifts') return 'hours';
    if (blocker === 'tables') return 'tables';
    return 'done';
  }
  if (staffEmail !== null) return 'done';
  return 'hours';
}

export type TReservationGuideAutoOpen = 'incomplete' | 'unused' | null;

/**
 * D23: opening the Reservas page lands on the guide for an enabled branch that
 * never took a reservation — every time while the setup is incomplete, once per
 * device once it is complete (the finish screen shows how to share the link).
 * An API build without the flag never triggers it.
 */
export function reservationGuideAutoOpen(
  summary: { enabled: boolean; hasReservations?: boolean },
  incomplete: boolean,
  shownBefore: boolean
): TReservationGuideAutoOpen {
  if (!summary.enabled || summary.hasReservations !== false) return null;
  if (incomplete) return 'incomplete';
  return shownBefore ? null : 'unused';
}

export function nextGuideScreen(
  current: TReservationGuideScreen
): TReservationGuideScreen {
  const index = SCREENS.indexOf(current);
  return SCREENS[Math.min(SCREENS.length - 1, index + 1)];
}

export function previousGuideScreen(
  current: TReservationGuideScreen
): TReservationGuideScreen | null {
  const index = SCREENS.indexOf(current);
  return index > 0 ? SCREENS[index - 1] : null;
}

export function reservationGuideFinishState(
  settings: Pick<IReservationSettings, 'enabled' | 'onlineEnabled'>
): TReservationGuideFinishState {
  if (!settings.enabled) return 'ready';
  return settings.onlineEnabled ? 'live' : 'phoneOnly';
}

/** Phone-only activation: one shift and one table staff can seat (internal counts). */
export function canActivateReservations(
  configuration: Pick<IReservationSettingsConfiguration, 'shifts' | 'tables'>
): boolean {
  return (
    configuration.shifts.length > 0 &&
    configuration.tables.some(isBookableReservationTable)
  );
}

/** Online activation: one shift and one enabled online table with seats. */
export function canActivateOnline(
  configuration: Pick<IReservationSettingsConfiguration, 'shifts' | 'tables'>
): boolean {
  return (
    configuration.shifts.length > 0 &&
    configuration.tables.some(
      (table) => isOnlineReservationTable(table) && table.capacity > 0
    )
  );
}

/** What blocks activation, in step order (shifts first). */
export function activationBlocker(
  configuration: Pick<IReservationSettingsConfiguration, 'shifts' | 'tables'>,
  online: boolean
): TActivationBlocker {
  if (configuration.shifts.length === 0) return 'shifts';
  const ok = online
    ? canActivateOnline(configuration)
    : canActivateReservations(configuration);
  return ok ? null : 'tables';
}

export function guidePreviewFocus(
  screen: TReservationGuideScreen,
  finish: TReservationGuideFinishState
): TGuestPreviewFocus {
  switch (screen) {
    case 'hours':
      return 'time';
    case 'tables':
    case 'groups':
      return 'party';
    case 'alerts':
      return 'contact';
    default:
      return finish === 'live' ? 'live' : 'off';
  }
}

/** Client-side sanity check only; the API's @IsEmail is the authority. */
export function isPlausibleEmail(value: string): boolean {
  return value.length <= 255 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/** Keeps digits and one leading "+". */
export function normalizePhoneInput(value: string): string {
  const trimmed = value.trim();
  const digits = trimmed.replace(/\D/g, '');
  return trimmed.startsWith('+') ? `+${digits}` : digits;
}

/** The normalised form has 6 to 20 digits. */
export function isPlausiblePhone(value: string): boolean {
  const digits = normalizePhoneInput(value).replace(/\D/g, '').length;
  return digits >= 6 && digits <= 20;
}

export const GUIDE_DURATION_CHIPS = [60, 90, 120, 150] as const;
export const GUIDE_LEAD_CHIPS = [0, 30, 60, 120, 240] as const;

/** The preset plus the stored value when it is not in the set, sorted. */
export function chipValues(
  preset: readonly number[],
  current: number
): number[] {
  return [...new Set([...preset, current])].sort((a, b) => a - b);
}

export const SEEDED_SHIFT_NAME = /^(Turno|Shift) \d+$/;

export interface IShiftProposal {
  key: 'lunch' | 'dinner';
  days: number[];
  from: string;
  to: string;
  slotIntervalMin: TReservationSlotInterval;
}

/** D14: the two local proposals shown when the branch has no opening hours. */
export function proposedReservationShifts(): IShiftProposal[] {
  return [
    {
      key: 'lunch',
      days: [0, 1, 2, 3, 4, 5, 6],
      from: '12:00',
      to: '16:00',
      slotIntervalMin: RESERVATION_DEFAULT_SLOT_INTERVAL_MIN,
    },
    {
      key: 'dinner',
      days: [0, 1, 2, 3, 4, 5, 6],
      from: '20:00',
      to: '00:00',
      slotIntervalMin: RESERVATION_DEFAULT_SLOT_INTERVAL_MIN,
    },
  ];
}

export type TWeekdaySummary =
  | { kind: 'none' }
  | { kind: 'all' }
  | { kind: 'range'; from: number; to: number }
  | { kind: 'list'; days: number[] };

/** Monday-first order of the API's Sunday = 0 weekday numbers. */
const WEEKDAYS_MON_FIRST: readonly number[] = [1, 2, 3, 4, 5, 6, 0];

/**
 * Monday..Sunday order, no week wrap: seven days → all; one contiguous run of
 * three or more → range; anything else → list; nothing → none.
 */
export function summarizeWeekdays(days: readonly number[]): TWeekdaySummary {
  const positions = [
    ...new Set(
      days
        .map((day) => WEEKDAYS_MON_FIRST.indexOf(day))
        .filter((position) => position >= 0)
    ),
  ].sort((a, b) => a - b);
  if (positions.length === 0) return { kind: 'none' };
  if (positions.length === 7) return { kind: 'all' };
  const first = positions[0];
  const last = positions[positions.length - 1];
  if (positions.length >= 3 && last - first + 1 === positions.length)
    return {
      kind: 'range',
      from: WEEKDAYS_MON_FIRST[first],
      to: WEEKDAYS_MON_FIRST[last],
    };
  return {
    kind: 'list',
    days: positions.map((position) => WEEKDAYS_MON_FIRST[position]),
  };
}

export interface IWeekdayLabelCopy {
  all: string;
  fromTo: (from: string, to: string) => string;
  list: (list: string, last: string) => string;
}

/** The only place a weekday summary becomes text (steps and the finish summary). */
export function weekdaySummaryLabel(
  summary: TWeekdaySummary,
  locale: string,
  copy: IWeekdayLabelCopy
): string {
  const names = shortWeekdaysMonFirst(locale).map((name) => {
    const clean = name.replace(/\.$/, '');
    return clean.charAt(0).toLocaleUpperCase(locale) + clean.slice(1);
  });
  const name = (day: number) => names[(day + 6) % 7];
  switch (summary.kind) {
    case 'none':
      return '';
    case 'all':
      return copy.all;
    case 'range':
      return copy.fromTo(name(summary.from), name(summary.to));
    default: {
      const days = summary.days.map(name);
      if (days.length === 1) return days[0];
      return copy.list(days.slice(0, -1).join(', '), days[days.length - 1]);
    }
  }
}

export function durationParts(
  minutes: number
): { hours: number; minutes: number } {
  const total = Math.max(0, Math.trunc(minutes));
  return { hours: Math.floor(total / 60), minutes: total % 60 };
}

/**
 * Numbering starts at 1 and skips names already taken (case-insensitive,
 * trimmed); capped at MAX_CREATED_TABLES. The caller passes how many tables
 * are still missing.
 */
export function nextTableNames(
  existing: readonly string[],
  count: number,
  label: (n: number) => string
): string[] {
  const taken = new Set(existing.map((name) => name.trim().toLowerCase()));
  const wanted = Math.min(MAX_CREATED_TABLES, Math.max(0, Math.trunc(count)));
  const names: string[] = [];
  // Every taken name blocks at most one number, so this bound always suffices.
  for (let n = 1; names.length < wanted && n <= wanted + taken.size; n++) {
    const name = label(n);
    if (!taken.has(name.trim().toLowerCase())) names.push(name);
  }
  return names;
}

/** Guest replica copy key; the `.voseo` twin for AR/UY Spanish (§9.3). */
export function guestCopyKey(
  key: 'overflowText' | 'offText' | 'frontSubtitle',
  voseo: boolean
): string {
  return `reservations.guide.guest.${key}${voseo ? '.voseo' : ''}`;
}
